import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSocket } from "@/lib/socket";
import { playChime, unlockAudio, playAlarm, isAudioUnlocked, subscribeAudio, playOrderChime, playReadyChime, playRequestChime, playBillChime } from "@/lib/chime";
import OrdersBoard from "@/components/dashboard/OrdersBoard";
import TablesGrid from "@/components/dashboard/TablesGrid";
import MenuManager from "@/components/dashboard/MenuManager";
import DailySpecialsPanel from "@/components/dashboard/DailySpecialsPanel";
import StockPanel from "@/components/dashboard/StockPanel";
import StatsPanel from "@/components/dashboard/StatsPanel";
import SettingsPanel from "@/components/dashboard/SettingsPanel";
import StaffPanel from "@/components/dashboard/StaffPanel";
import { SortableNav } from "@/components/dashboard/SortableNav";
import PaymentsPanel from "@/components/dashboard/PaymentsPanel";
import FiscalPanel from "@/components/dashboard/FiscalPanel";
import ArchivesExport from "@/components/dashboard/ArchivesExport";
import { ConnectingOverlay } from "@/components/common/ConnectingOverlay";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ClipboardList, LayoutGrid, UtensilsCrossed, BarChart3, Settings, Home, Sun, Boxes, Users,
  Volume2, VolumeX, Bell, Wifi, WifiOff, X, LogOut, ChevronDown, Store, CreditCard, Star, ScrollText, LifeBuoy, Landmark, Archive,
} from "lucide-react";
import Cockpit from "@/components/dashboard/Cockpit";
import ActivityLog from "@/components/dashboard/ActivityLog";
import UserGuide from "@/components/dashboard/UserGuide";
import { StockAlertBanner, useStockAlerts } from "@/components/common/StockAlertBanner";
import SyncStatus from "@/components/SyncStatus";
import NotifBadge from "@/components/common/NotifBadge";
import { StationSwitcher, useCompactHeader } from "@/components/common/StationSwitcher";
import { acceptEvent } from "@/lib/notify";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useWebPush } from "@/hooks/useWebPush";
import { useFeatures } from "@/lib/features";

const NAV = [
  { key: "accueil", label: "Accueil", icon: Home },
  { key: "orders", label: "Commandes", icon: ClipboardList },
  { key: "payments", label: "Paiements", icon: CreditCard },
  { key: "tables", label: "Tables", icon: LayoutGrid },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "specials", label: "Plat du jour", icon: Sun },
  { key: "stock", label: "Stocks", icon: Boxes },
  { key: "stats", label: "Statistiques", icon: BarChart3 },
  { key: "journal", label: "Journal", icon: ScrollText },
  { key: "fiscal", label: "Fiscal", icon: Landmark },
  { key: "export", label: "Archives & Export", icon: Archive },
  { key: "aide", label: "Aide", icon: LifeBuoy },
  { key: "staff", label: "Employés", icon: Users },
  { key: "settings", label: "Paramètres", icon: Settings },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const compact = useCompactHeader();
  // Role-based landing: Manager space is for owner/manager only. Kitchen -> KDS, Server -> POS.
  useEffect(() => {
    if (user?.role === "kitchen") navigate("/kds", { replace: true });
    else if (user?.role === "server") navigate("/pos", { replace: true });
  }, [user, navigate]);
  const isAdmin = user?.role === "admin" || user?.role === "org_admin";
  const isManager = user?.role === "manager";
  const [tab, setTab] = useState((user?.role === "admin" || user?.role === "org_admin" || user?.role === "manager") ? "accueil" : "orders");
  useEffect(() => { const h = (e) => setTab(e.detail); window.addEventListener("novanext:go-tab", h); return () => window.removeEventListener("novanext:go-tab", h); }, []);
  const [restaurants, setRestaurants] = useState([]);
  const [slug, setSlug] = useState(null);
  const feats = useFeatures(slug);
  const FEATURE_OF = { payments: "payments", specials: "specials", stock: "stock", stats: "stats" };
  const navItems = (isAdmin ? NAV
    : isManager ? NAV.filter((n) => n.key !== "settings")
    : NAV.filter((n) => n.key === "orders" || n.key === "payments")).filter((n) => !FEATURE_OF[n.key] || feats.has(FEATURE_OF[n.key]));
  const [restaurant, setRestaurant] = useState(null);
  const navOrder = restaurant?.manager_nav_order?.length ? restaurant.manager_nav_order : null;
  const saveNavOrder = async (order) => {
    try { const r = await api.setManagerNav(slug, order || []); setRestaurant((p) => ({ ...p, manager_nav_order: r.manager_nav_order })); toast.success(order ? "Menu réorganisé" : "Ordre du menu réinitialisé"); }
    catch { toast.error("Enregistrement impossible"); }
  };
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState({ key: "today", from: null, to: null });
  const [serviceRequests, setServiceRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsOverview, setPaymentsOverview] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [negativeAlerts, setNegativeAlerts] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(isAudioUnlocked());
  useEffect(() => subscribeAudio(setAudioReady), []);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const soundRef = useRef(true);
  soundRef.current = soundOn;

  const loadOrders = () => slug && api.listOrders(slug).then(setOrders).catch(() => {});
  const loadTables = () => slug && (isAdmin || isManager) && api.getTables(slug).then(setTables).catch(() => {});
  const loadMenu = () => slug && (isAdmin || isManager) && api.getMenu(slug, true, true).then(setMenu).catch(() => {});
  const loadStats = (r = statsRange) => slug && (isAdmin || isManager) && api.getStats(slug, { range: r.key, from: r.from || undefined, to: r.to || undefined }).then(setStats).catch(() => {});
  const loadRequests = () => slug && api.listServiceRequests(slug).then(setServiceRequests).catch(() => {});
  const loadPayments = () => slug && api.listPayments(slug).then(setPayments).catch(() => {});
  const loadOverview = () => slug && api.paymentsOverview(slug).then(setPaymentsOverview).catch(() => {});
  const loadReviewSummary = () => slug && (isAdmin || isManager) && api.reviewsSummary(slug).then(setReviewSummary).catch(() => setReviewSummary(null));

  // pick restaurants + initial slug
  // slug choisi depuis l'Espace Groupe : lu une seule fois (l'effet peut se relancer quand `user` change)
  const pendingSlug = useRef(localStorage.getItem("novanext_active_slug"));
  useEffect(() => {
    api.listRestaurants().then((rs) => {
      setRestaurants(rs);
      const stored = pendingSlug.current;
      const initial = user?.role === "server"
        ? user.restaurant_slug
        : (rs.find((r) => r.slug === stored)?.slug || rs.find((r) => r.slug === "comptoir-parisien")?.slug || rs[0]?.slug);
      localStorage.removeItem("novanext_active_slug");
      setSlug(initial || null);
    }).catch(() => {});
  }, [user]);

  // load data whenever slug changes
  useEffect(() => {
    if (!slug) return;
    api.getRestaurant(slug).then(setRestaurant).catch(() => {});
    loadOrders(); loadTables(); loadMenu(); loadStats(); loadRequests(); loadPayments(); loadOverview(); loadReviewSummary();
    // eslint-disable-next-line
  }, [slug]);

  // Rafraîchissement ciblé au changement d'onglet (pas de polling agressif : le temps réel
  // passe par les événements WebSocket ci-dessous).
  useWakeLock();
  useWebPush(slug);
  useEffect(() => {
    if (!slug) return;
    if (tab === "payments") loadOverview();
    if (tab === "stats") { loadStats(statsRange); loadReviewSummary(); }
    // eslint-disable-next-line
  }, [tab, slug]);

  useEffect(() => {
    if (tab === "stats") loadStats(statsRange);
    // eslint-disable-next-line
  }, [statsRange]);

  useEffect(() => {
    const names = { accueil: "Accueil", orders: "Commandes", tables: "Tables", stats: "Statistiques", menu: "Menu", payments: "Paiements", journal: "Journal", settings: "Paramètres" };
    document.title = `NOVANEXT TABLE · ${names[tab] || "POSTE"}`;
  }, [tab]);

  const stock = useStockAlerts(slug, soundRef);
  const [menuTick, setMenuTick] = useState(0);
  const [stockTick, setStockTick] = useState(0);
  const { connected } = useSocket(slug ? [`resto:${slug}`] : [], {
    ...stock.handlers,
    features_updated: () => { feats.reload(); toast.info("Fonctionnalités mises à jour par NOVANEXT"); },
    new_order: (order) => {
      setOrders((prev) => prev.find((o) => o.id === order.id) ? prev : [order, ...prev]);
      setNewOrderIds((prev) => new Set(prev).add(order.id));
      setTimeout(() => setNewOrderIds((prev) => { const n = new Set(prev); n.delete(order.id); return n; }), 5000);
      if (acceptEvent(`order:${order.id}:${order.status}`)) {
        if (soundRef.current) playOrderChime();
        toast.success(`🔔 Nouvelle commande · Table ${order.table_number}`, { description: order.order_code || order.order_number });
      }
      loadTables();
    },
    order_updated: (order) => {
      setOrders((prev) => prev.map((o) => (o.id === order.id ? order : o)));
      loadTables();
    },
    order_reminder: (o) => {
      if ((o.reminder_count || 0) >= 2) return; // à la 2e relance, l'escalade Manager prend le relais
      if (!acceptEvent(`remind:${o.id}:${o.reminder_count}`)) return;
      if (soundRef.current) playReadyChime();
      toast.warning(`⏰ Plat prêt en attente · Table ${o.table_number}`, { description: `${o.order_code || o.order_number} · relance ${o.reminder_count}`, duration: 15000 });
    },
    order_escalation: (o) => {
      if (!acceptEvent(`escal:${o.id}`)) return;
      const chan = o.channel || "both";
      if (soundRef.current && (chan === "sound" || chan === "both")) playChime();
      toast.error(`🚨 Escalade · Table ${o.table_number} — plat prêt non servi`, {
        id: `escal-${o.id}`, duration: Infinity,
        description: `${o.order_code || o.order_number} · à traiter en priorité`,
      });
    },
    resync: () => { loadOrders(); loadTables(); loadRequests(); loadPayments(); },
    service_request: (req) => {
      setServiceRequests((prev) => prev.find((r) => r.id === req.id) ? prev : [req, ...prev]);
      if (acceptEvent(`sr:${req.id}`)) {
        if (soundRef.current) playRequestChime(req.type);
        toast.warning(`🖐 Table ${req.table_number} demande un serveur`);
      }
      loadTables();
    },
    service_request_updated: () => { loadRequests(); loadTables(); },
    menu_updated: () => { loadMenu(); stock.handlers.menu_updated(); setMenuTick((t) => t + 1); },
    stock_alert: (a) => { stock.handlers.stock_alert(a); setMenuTick((t) => t + 1); setStockTick((t) => t + 1); },
    stock_alert_cleared: (a) => { stock.handlers.stock_alert_cleared(a); setMenuTick((t) => t + 1); setStockTick((t) => t + 1); },
    stock_updated: () => setStockTick((t) => t + 1),
    settings_updated: (r) => setRestaurant(r),
    payment_request: (p) => {
      loadPayments(); loadOverview();
      if (soundRef.current) playBillChime();
      toast.warning(`💳 Table ${p.table_number} souhaite régler au comptoir`);
    },
    payment_settled: (p) => {
      loadPayments(); loadOverview();
      loadTables();
      if (p && p.fully_paid) toast.success(`✅ Table ${p.table_number} entièrement réglée`);
    },
    payment_failed: (p) => {
      loadPayments(); loadOverview();
      if (!acceptEvent(p.event_id || `payfail:${p?.table_number}`)) return;
      if (soundRef.current) playAlarm();
      toast.error(`⛔ Paiement échoué · Table ${p.table_number}`, {
        description: `Reste ${(p.remaining || 0).toFixed(2)} €${p.detail ? " · " + p.detail : ""}`, duration: 20000,
      });
    },
    negative_review: (r) => {
      setNegativeAlerts((prev) => {
        const key = r.id || `${Date.now()}`;
        if (prev.some((x) => x.key === key)) return prev;
        return [{ ...r, key }, ...prev].slice(0, 20);
      });
      loadReviewSummary();
      if (soundRef.current) playChime();
      const where = r.table_number ? `Table ${r.table_number}` : "Un client";
      toast.error(`⚠️ Avis ${r.rating}★ · ${where}`, { description: r.comment || "Réagissez avant le départ du client", duration: 10000 });
    },
  });

  // Filet de réconciliation : recharge périodique INDÉPENDANTE de l'état "connected"
  // (un socket "zombie" garde connected=true sans recevoir les events). Garantit la fraîcheur sans F5.
  useEffect(() => {
    if (!slug) return;
    const id = setInterval(() => {
      loadOrders(); loadTables(); loadRequests(); loadPayments();
      if (tab === "payments") loadOverview();
      if (tab === "stats") { loadStats(statsRange); loadReviewSummary(); }
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [tab, slug, statsRange]);

  const handleStatus = async (id, status) => {
    try {
      const updated = await api.updateOrderStatus(id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
      loadTables(); loadStats();
    } catch (e) { toast.error("Erreur"); }
  };

  const itemStatus = async (id, status, idx) => {
    try { const o = await api.updateItemStatus(id, status, idx); setOrders((prev) => prev.map((x) => (x.id === id ? o : x))); loadTables(); loadStats(); } catch (e) { toast.error("Erreur"); }
  };

  const takeService = async (id) => {
    try { const o = await api.takeOrderService(id); setOrders((prev) => prev.map((x) => (x.id === id ? o : x))); } catch (e) { toast.error("Erreur"); }
  };

  const handleRequest = async (id) => {
    try {
      await api.handleServiceRequest(id);
      setServiceRequests((prev) => prev.filter((r) => r.id !== id));
      loadTables();
      toast.success("Demande terminée");
    } catch (e) { toast.error("Erreur"); }
  };

  const handleClaim = async (id) => {
    try {
      const r = await api.claimServiceRequest(id);
      setServiceRequests((prev) => prev.map((x) => (x.id === id ? r : x)));
      toast.success("Demande prise en charge");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        if (d?.request) setServiceRequests((prev) => prev.map((x) => (x.id === id ? d.request : x)));
        toast.info(`Déjà prise en charge par ${d?.claimed_by || "un collègue"}`);
      } else { toast.error("Erreur"); }
    }
  };

  const enableSound = () => { unlockAudio(); setAudioReady(true); playChime(); };
  const switchRestaurant = (s) => { setSwitcherOpen(false); setTab("orders"); setSlug(s); };

  const activeCount = orders.filter((o) => o.status === "NOUVELLE").length;
  const pendingPayCount = payments.filter((p) => p.method === "cash" && p.payment_status === "pending").length;

  if (!slug || !restaurant) {
    return <ConnectingOverlay message="Préparation de votre espace…" />;
  }

  return (
    <div className="dark-scroll min-h-screen bg-[#0C1713] text-stone-100 flex flex-col md:flex-row" style={{ overflowX: "clip" }}>
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-[#08110E] border-b md:border-b-0 md:border-r border-[#1B3028] p-4 md:p-5 flex md:flex-col md:justify-between md:min-h-screen shrink-0">
        <div className="flex-1 md:flex-none">
          <div className="flex items-center gap-2.5 mb-0 md:mb-8">
            <div className="w-9 h-9 rounded-lg bg-[#C69C4A] text-[#0C1713] grid place-items-center font-serif-display font-bold text-lg">N</div>
            <div className="hidden md:block">
              <p className="font-serif-display text-lg leading-none">NOVANEXT</p>
              <p className="text-[10px] uppercase tracking-widest text-[#C69C4A]">{isAdmin ? "Table · Poste" : isManager ? "Manager" : "Serveur"}</p>
            </div>
          </div>
          <nav className="flex md:flex-col gap-1.5 ml-auto md:ml-0 overflow-x-auto no-scrollbar">
            <SortableNav items={navItems} order={navOrder} onReorder={saveNavOrder} onReset={navOrder ? () => saveNavOrder(null) : null} renderItem={(n, handle) => {
              const Icon = n.icon;
              const active = tab === n.key;
              return (
                <button
                  key={n.key}
                  data-testid={`nav-${n.key}`}
                  onClick={() => setTab(n.key)}
                  className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors w-full ${
                    active ? "bg-[#1A3D32] text-white" : "text-stone-400 hover:text-stone-100 hover:bg-[#111F1A]"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{n.label}</span>
                  {n.key === "orders" && activeCount > 0 && (
                    <span className="ml-1 bg-[#C69C4A] text-[#0C1713] text-[10px] font-bold rounded-full px-1.5 py-0.5">{activeCount}</span>
                  )}
                  {n.key === "payments" && pendingPayCount > 0 && (
                    <span className="ml-1 bg-[#C69C4A] text-[#0C1713] text-[10px] font-bold rounded-full px-1.5 py-0.5">{pendingPayCount}</span>
                  )}
                  {handle}
                </button>
              );
            }} />
          </nav>
        </div>
        <div className="hidden md:flex flex-col gap-3 mt-4">
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <SyncStatus connected={connected} />
            <NotifBadge slug={slug} />
          </div>
          <div className="border-t border-[#1B3028] pt-3">
            <p data-testid="sidebar-user-identity" className="text-xs text-stone-400 truncate mb-2">{user?.name || user?.email}{user?.employee_code && <span className="font-mono text-stone-300"> · {user.employee_code}</span>} · <span className="text-[#C69C4A]">{user?.role === "org_admin" ? "Admin groupe" : isAdmin ? "Propriétaire" : isManager ? "Manager" : "Serveur"}</span></p>
            <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-xs text-stone-400 hover:text-red-400 font-medium">
              <LogOut className="w-3.5 h-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className={`sticky top-0 z-30 bg-[#0C1713]/95 backdrop-blur border-b border-[#1B3028] px-4 lg:px-6 flex items-center justify-between gap-3 transition-[padding] duration-200 ${compact ? "py-1.5" : "py-3"}`}>
          <div className="min-w-0">
            <h1 className="font-serif-display text-xl lg:text-2xl">{navItems.find((n) => n.key === tab)?.label}</h1>
            {/* restaurant switcher */}
            {isAdmin && restaurants.length > 0 ? (
              <div className="relative">
                <button data-testid="restaurant-switcher" onClick={() => setSwitcherOpen((o) => !o)} className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-200">
                  <Store className="w-3 h-3 text-[#C69C4A]" /> {restaurant.name} <ChevronDown className="w-3 h-3" />
                </button>
                {switcherOpen && (
                  <div data-testid="restaurant-switcher-menu" className="absolute left-0 top-6 z-40 w-56 rounded-xl bg-[#111F1A] border border-[#1B3028] py-1 shadow-2xl">
                    {restaurants.map((r) => (
                      <button key={r.slug} data-testid={`switch-to-${r.slug}`} onClick={() => switchRestaurant(r.slug)} className={`w-full text-left px-3 py-2 text-sm hover:bg-[#1A3D32] ${r.slug === slug ? "text-[#C69C4A]" : "text-stone-300"}`}>
                        {r.name}
                      </button>
                    ))}
                    {restaurants.length > 1 || user?.role === "org_admin" ? <button data-testid="open-group-space" onClick={() => navigate("/group")} className="w-full text-left px-3 py-2 text-sm border-t border-[#1B3028] text-[#C69C4A] hover:bg-[#1A3D32]">Vue groupe · tous mes établissements</button> : null}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-stone-500">{restaurant?.name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!audioReady && (
              <Button data-testid="enable-sound-btn" onClick={enableSound} size="sm" className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold text-xs">
                <Volume2 className="w-3.5 h-3.5 mr-1.5" /> Activer le son
              </Button>
            )}
            <button
              data-testid="sound-toggle"
              onClick={() => setSoundOn((s) => !s)}
              className={`w-9 h-9 rounded-full grid place-items-center ${soundOn ? "bg-[#1A3D32] text-[#C69C4A]" : "bg-[#111F1A] text-stone-500"}`}
            >
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <StationSwitcher current="manager" slug={slug} />
          </div>
        </div>

        {/* Negative review alert banner */}
        {negativeAlerts.length > 0 && (
          <div className="px-4 lg:px-6 pt-4">
            <div data-testid="negative-review-banner" className="rounded-2xl bg-[#1C1210] border border-amber-500/50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400 shrink-0 fill-amber-400" />
                <span className="text-sm font-semibold text-amber-300">Avis clients à surveiller — réagissez maintenant :</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {negativeAlerts.map((a) => (
                  <div key={a.key} data-testid={`negative-alert-${a.key}`} className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 text-amber-100 text-xs font-semibold px-3 py-1.5 max-w-full">
                    <span className="font-bold">{a.rating}★</span>
                    {a.table_number && <span className="text-amber-300/80">Table {a.table_number}</span>}
                    {a.comment && <span className="truncate max-w-[240px] font-normal text-amber-200/90">« {a.comment} »</span>}
                    <button data-testid={`negative-alert-dismiss-${a.key}`} onClick={() => setNegativeAlerts((prev) => prev.filter((x) => x.key !== a.key))} className="text-amber-300 hover:text-white"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Service request banner */}
        {serviceRequests.length > 0 && (
          <div className="px-4 lg:px-6 pt-4">
            <div className="rounded-2xl bg-[#1C1210] border border-red-500/50 p-3 flex flex-wrap gap-2 items-center">
              <Bell className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-sm font-semibold text-red-300">Demandes de service :</span>
              {serviceRequests.map((r) => (
                <button
                  key={r.id}
                  data-testid={`service-request-${r.table_number}`}
                  onClick={() => handleRequest(r.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-200 text-xs font-semibold px-3 py-1.5"
                >
                  Table {r.table_number} — <span className="font-bold uppercase">{r.label || r.type || "Serveur"}</span> <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Payment (cash) request banner */}
        {pendingPayCount > 0 && (
          <div className="px-4 lg:px-6 pt-4">
            <div className="rounded-2xl bg-[#12140C] border border-[#C69C4A]/50 p-3 flex flex-wrap gap-2 items-center">
              <CreditCard className="w-4 h-4 text-[#C69C4A] shrink-0" />
              <span className="text-sm font-semibold text-[#E7C97A]">Encaissements en attente :</span>
              {payments.filter((p) => p.method === "cash" && p.payment_status === "pending").map((p) => (
                <button
                  key={p.id}
                  data-testid={`payment-banner-${p.table_number}`}
                  onClick={() => setTab("payments")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#C69C4A]/15 hover:bg-[#C69C4A]/25 text-[#E7C97A] text-xs font-semibold px-3 py-1.5"
                >
                  Table {p.table_number} · {Number(p.amount).toFixed(2).replace(".", ",")} €
                </button>
              ))}
            </div>
          </div>
        )}

        <StockAlertBanner alerts={stock.alerts} onDismiss={stock.dismiss} />
        <div className="p-4 lg:p-6">
          {(isAdmin || isManager) && tab === "accueil" && <Cockpit slug={slug} onNavigate={setTab} requests={serviceRequests} onClaim={handleClaim} onHandle={handleRequest} />}
          {tab === "orders" && <OrdersBoard orders={orders} onStatus={handleStatus} newOrderIds={newOrderIds} slug={slug} payments={payments} onTakeService={takeService} readyAlertMin={restaurant?.ready_alert_minutes || 3} onItemStatus={itemStatus} />}
          {tab === "payments" && <PaymentsPanel slug={slug} payments={payments} overview={paymentsOverview} onRefresh={() => { loadPayments(); loadOverview(); loadTables(); }} isAdmin={isAdmin || isManager} />}
          {(isAdmin || isManager) && tab === "tables" && <TablesGrid tables={tables} slug={slug} onRefresh={loadTables} />}
          {(isAdmin || isManager) && tab === "menu" && <MenuManager slug={slug} products={menu.products} categories={menu.categories} onRefresh={loadMenu} />}
          {(isAdmin || isManager) && tab === "specials" && <DailySpecialsPanel slug={slug} products={menu.products} refreshKey={menuTick} />}
          {(isAdmin || isManager) && tab === "stock" && <StockPanel slug={slug} refreshKey={stockTick} products={menu.products} features={feats} />}
          {(isAdmin || isManager) && tab === "stats" && <StatsPanel slug={slug} stats={stats} range={statsRange} onRange={setStatsRange} onNavigate={setTab} reviewSummary={reviewSummary} />}
          {(isAdmin || isManager) && tab === "journal" && <ActivityLog slug={slug} onNavigate={setTab} />}
          {(isAdmin || isManager) && tab === "fiscal" && <FiscalPanel slug={slug} isAdmin={isAdmin} />}
          {(isAdmin || isManager) && tab === "export" && <ArchivesExport slug={slug} />}
          {(isAdmin || isManager) && tab === "aide" && <UserGuide restaurant={restaurant} />}
          {(isAdmin || isManager) && tab === "staff" && (
            <div data-testid="staff-tab">
              <h2 className="font-serif-display text-2xl text-stone-100 mb-1">Employés & codes PIN</h2>
              <p className="text-stone-400 text-sm mb-4">Créez vos serveurs, cuisiniers et managers, attribuez un PIN individuel, activez ou désactivez un accès sans perdre l'historique.</p>
              <StaffPanel slug={slug} />
            </div>
          )}
          {isAdmin && tab === "settings" && (
            <SettingsPanel
              restaurant={restaurant}
              slug={slug}
              restaurants={restaurants}
              soundOn={soundOn}
              setSoundOn={setSoundOn}
              onBrandUpdated={(r) => { setRestaurant(r); setRestaurants((prev) => prev.map((x) => x.slug === r.slug ? r : x)); }}
              onRestaurantsChanged={(r) => { setRestaurants((prev) => [...prev, r]); switchRestaurant(r.slug); }}
            />
          )}
        </div>
      </main>
    </div>
  );
}
